import { forwardRef, useState } from 'react';

import Button from 'components/button/Button';
import CheckboxGroup from 'components/checkbox-group/CheckboxGroup';
import ContentArea from 'components/content-area/ContentArea';
import Form from 'components/form/Form';
import PageFooter from 'components/page-footer/PageFooter';
import RichText from 'components/rich-text/RichText';
import InputEmail from 'components/input-email/InputEmail';
import Message from 'components/message/Message';
import ContentContainer from 'components/content-container/ContentContainer';
import { ContentWithSidebar, Sidebar, Content } from 'components/content-with-sidebar';
import GenericTextInputGroup from 'components/text-input-group/TextInputWithHeader';

import { INewsletterRegistrationPage, IMessage } from 'source/NFR.types';

interface NewsletterRegistrationPageProps extends INewsletterRegistrationPage {
  onSubmit?: (e: React.FormEvent) => void;
  showFormError?: boolean;
  setIsChecked?: (checked: boolean) => void;
  showEmailError?: boolean;
  setIsValidEmail: (valid: boolean) => void;
}

const NewsletterRegistrationPage = forwardRef<HTMLDivElement, NewsletterRegistrationPageProps>(
  (
    {
      checkboxGroups = [],
      genericTextInputGroup,
      footer,
      form,
      inputFields,
      inputTitle,
      richText,
      submitButton,
      htmlString,
      title,
      sidebar,
      onSubmit,
      showFormError = true,
      setIsChecked,
      showEmailError,
      setIsValidEmail
    },
    ref
  ) => {
    const [message, setMessage] = useState<IMessage | undefined>();

    return (
      <Form className="newsletter-registration-page" showSubmitButton={false} onSubmit={onSubmit} {...form}>
        {htmlString && <div dangerouslySetInnerHTML={{ __html: htmlString }} />}
        <ContentContainer>{title && <h1>{title}</h1>}</ContentContainer>

        <ContentWithSidebar>
          <Content>
            <RichText {...richText} />
            <div className="newsletter-registration-page--message" ref={ref}>
              {showFormError && message && <Message {...message} />}
            </div>
            {checkboxGroups?.map(checkboxGroup => (
              <CheckboxGroup
                key={checkboxGroup.title}
                setIsChecked={checkboxGroup.required ? setIsChecked : undefined}
                setMessage={setMessage}
                {...checkboxGroup}
              />
            ))}
            {genericTextInputGroup && (
              <div className="newsletter-registration-page--group">
                <GenericTextInputGroup
                  key={genericTextInputGroup.title}
                  title={genericTextInputGroup.title}
                  options={genericTextInputGroup.options}
                />
              </div>
            )}
            {inputTitle && <h2 className="newsletter-registration-page--email-title">{inputTitle}</h2>}
            {inputFields?.map(inputField => (
              <InputEmail
                key={inputField.name}
                isValid={!showEmailError}
                setIsValid={setIsValidEmail}
                {...inputField}
              />
            ))}
            <Button
              className="newsletter-registration-page--submit"
              theme={['primary', 'large']}
              type="submit"
              {...submitButton}
            />
            <div className="newsletter-registration-page--footer">
              <PageFooter {...footer} />
            </div>
          </Content>
          <Sidebar>
            <ContentArea enableElementSizing={false} {...sidebar} />
          </Sidebar>
        </ContentWithSidebar>
      </Form>
    );
  }
);

NewsletterRegistrationPage.displayName = 'NewsletterRegistrationPage';

export default NewsletterRegistrationPage;
